import axios from 'axios';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest } from './authConfig';
import { msalInstance } from './msalInstance';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── IdP hint helper ───────────────────────────────────────────────────────────
//
// For Entra External ID (CIAM) with federated Google accounts, the ID token
// contains an `idp` claim identifying the originating identity provider.
// We map that claim to the `domain_hint` Entra needs to skip its own login
// page and route directly back to the correct IdP.
//
// Without this hint, Entra shows a password prompt for the internal shadow
// account it created to represent the federated user — which is confusing
// and wrong for Google/Microsoft login flows.

const IDP_TO_DOMAIN_HINT: Record<string, string> = {
  'google.com': 'google.com',
  'https://accounts.google.com': 'google.com',
  'microsoft.com': 'microsoft.com',
  'live.com': 'live.com',
  'https://login.live.com': 'live.com',
};

function getDomainHint(account: ReturnType<typeof msalInstance.getAllAccounts>[number]): string | undefined {
  const claims = account.idTokenClaims as Record<string, unknown> | undefined;
  const idp = typeof claims?.idp === 'string' ? claims.idp : undefined;
  return idp ? IDP_TO_DOMAIN_HINT[idp] : undefined;
}

// ── Re-auth via redirect ──────────────────────────────────────────────────────
//
// When acquireTokenSilent fails with InteractionRequiredAuthError, we use
// acquireTokenRedirect so re-auth stays entirely within the current tab (no
// popup). redirectStartPage tells Entra to return the user to whatever page
// they were on when the token expired, so they land back in the right place.
//
// With the localStorage token cache, silent refresh handles normal expiry
// invisibly — this redirect path is a last-resort safety net only.
//
// On a cold load with an expired session, ~10 queries fire at once and every
// one lands here. Without a guard, each fires its own acquireTokenRedirect and
// the extra navigations bounce the user back to Entra a second time. This flag
// ensures only the FIRST request triggers the redirect; the page is navigating
// away so it never needs to be reset.

let interactiveRedirectInFlight = false;

function reAuthenticate(account: ReturnType<typeof msalInstance.getAllAccounts>[number]): void {
  if (interactiveRedirectInFlight) return;
  interactiveRedirectInFlight = true;

  const domainHint = getDomainHint(account);
  console.warn(
    'Session expired or interaction required — re-authenticating via redirect...',
    domainHint ? `(domain_hint: ${domainHint})` : '(no domain hint found)'
  );

  msalInstance.acquireTokenRedirect({
    ...loginRequest,
    account,
    // loginHint pre-fills the account so the user doesn't have to type it.
    loginHint: account.username,
    // domain_hint routes Google/Microsoft users to the correct IdP instead
    // of showing Entra's internal-account password prompt.
    ...(domainHint ? { extraQueryParameters: { domain_hint: domainHint } } : {}),
    // Return the user to the exact page they were on after reauth.
    redirectStartPage: window.location.href,
  }).catch((redirectError) => {
    console.error('Re-authentication redirect failed:', redirectError);
  });
}

// ── Request interceptor ───────────────────────────────────────────────────────
//
// Acquires a valid ID token from MSAL before every API call.
//
// Flow:
//   1. Try acquireTokenSilent — succeeds most of the time because tokens are
//      cached in localStorage (survives refreshes and idle periods).
//   2. If InteractionRequiredAuthError, call reAuthenticate() which initiates
//      a redirect to Entra. The page navigates away so there is no token to
//      attach — the request is abandoned and the user returns after reauth.
//   3. For all other silent-refresh failures, continue without a token and let
//      the backend 401 surface to the UI via React Query.

apiClient.interceptors.request.use(
  async (config) => {
    // An interactive redirect is already underway — the browser is navigating
    // away. Cancel this request rather than firing it token-less (guaranteed
    // 401) and provoking React Query retries.
    if (interactiveRedirectInFlight) {
      throw new axios.Cancel('Auth redirect in progress');
    }

    const account =
      msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];

    if (account) {
      try {
        const tokenResponse = await msalInstance.acquireTokenSilent({
          ...loginRequest,
          account,
        });

        if (tokenResponse.idToken) {
          config.headers.Authorization = `Bearer ${tokenResponse.idToken}`;
        }
      } catch (tokenError) {
        if (tokenError instanceof InteractionRequiredAuthError) {
          // Kick off redirect reauth — page navigates away, no token to attach.
          reAuthenticate(account);
        } else {
          console.warn('Silent token acquisition failed (non-interaction error):', tokenError);
        }
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor ──────────────────────────────────────────────────────
//
// 401 means the backend rejected the token (expired, wrong issuer, etc.).
// We log it for diagnosis but do NOT trigger another popup here — that
// could cause a redirect loop or show the wrong Entra prompt again.
// React Query will surface the error to the UI, and the user can use the
// Sign In button to re-authenticate cleanly.

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.warn(
        'API returned 401 — token was rejected by the backend.',
        error.response?.headers?.['www-authenticate']
      );
    }
    return Promise.reject(error);
  }
);

export default apiClient;
